// 振り返りメモ (KPT + 自由記述) の純粋ロジック。
// localStorage キャッシュ・旧 KPT キーからの移行・前期間計算を担い、DOM / Supabase には依存しない。

export type RetroMode = "day" | "week" | "month";

export interface RetroDraft {
  keep: string;
  problem: string;
  tryNext: string;
  freeNote: string;
}

export interface CachedRetro extends RetroDraft {
  savedAt: number;
}

export interface RetrospectiveRecord extends RetroDraft {
  id: string;
  mode: RetroMode;
  periodStart: string;
  /** epoch ms */
  updatedAt: number;
}

/**
 * 振り返り 1 件を一意に決めるスコープ。
 *
 * workspaceId は「チーム WS の id」か「個人 WS を表す null」で、**省略できない**。
 * 未指定を個人 WS 扱いにフォールバックすると、WS 未解決のまま読み書きしたときに
 * チームの振り返りが個人の行へ流れ込む (逆も然り)。呼び出し側は WS が確定するまで
 * 読み書きしないこと。
 *
 * 個人 WS を null で表すのは、workspace_id 追加 (20260723000000) より前に書かれた
 * 個人の行が workspace_id = null で保存されているため。ここを個人 WS の id に
 * 変えると既存の振り返りが読めなくなる。
 */
export interface RetroScope {
  /** チーム WS の id。個人 WS は null */
  workspaceId: string | null;
  /** 表示対象メンバーの user_id。未指定なら自分 */
  targetUserId?: string;
}

/** scope が未指定 / workspaceId 省略のまま渡されていないか検証する */
export function assertRetroScope(scope: RetroScope | undefined, fnName: string): RetroScope {
  if (!scope || scope.workspaceId === undefined) {
    throw new Error(
      `${fnName} requires an explicit workspaceId (null for the personal workspace)`
    );
  }
  return scope;
}

/** localStorage 互換の最小インターフェース (テスト差し替え用) */
export type StorageLike = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem" | "key"
> & { length: number };

export function emptyDraft(): RetroDraft {
  return { keep: "", problem: "", tryNext: "", freeNote: "" };
}

export function isDraftEmpty(draft: RetroDraft): boolean {
  return !draft.keep && !draft.problem && !draft.tryNext && !draft.freeNote;
}

/**
 * キャッシュキー。DB 側と同じくワークスペースごとに分ける。
 *
 * 分けないと、個人 WS で書いた下書きがチーム WS を開いたときにヒットし、
 * 「キャッシュの方が新しい」判定でチームの行へ上書き保存されてしまう
 * (DB 側だけ直しても症状が残る)。個人 WS (null) は従来キーのままにして、
 * 既存の下書きを捨てないようにしている。
 */
export function retroCacheKey(
  mode: RetroMode,
  periodStart: string,
  scope: RetroScope
): string {
  const { workspaceId, targetUserId } = assertRetroScope(scope, "retroCacheKey");
  let key = `donezo-retro-${mode}-${periodStart}`;
  if (targetUserId) key += `-u-${targetUserId}`;
  if (workspaceId) key += `-w-${workspaceId}`;
  return key;
}

/** 前の期間の開始日キー (day: 前日 / week: 7日前 / month: 前月1日) */
export function prevPeriodStart(mode: RetroMode, periodStart: string): string {
  const [y, m, d] = periodStart.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (mode === "day") {
    date.setDate(date.getDate() - 1);
  } else if (mode === "week") {
    date.setDate(date.getDate() - 7);
  } else {
    date.setDate(1);
    date.setMonth(date.getMonth() - 1);
  }
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function toStringField(v: unknown): string {
  return typeof v === "string" ? v : "";
}

export function readCachedRetro(
  storage: StorageLike,
  mode: RetroMode,
  periodStart: string,
  scope: RetroScope
): CachedRetro | null {
  try {
    const raw = storage.getItem(retroCacheKey(mode, periodStart, scope));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const p = parsed as Record<string, unknown>;
    return {
      keep: toStringField(p.keep),
      problem: toStringField(p.problem),
      tryNext: toStringField(p.tryNext),
      freeNote: toStringField(p.freeNote),
      savedAt: typeof p.savedAt === "number" ? p.savedAt : 0,
    };
  } catch {
    return null;
  }
}

export function writeCachedRetro(
  storage: StorageLike,
  mode: RetroMode,
  periodStart: string,
  draft: RetroDraft,
  savedAt: number,
  scope: RetroScope
): void {
  try {
    storage.setItem(
      retroCacheKey(mode, periodStart, scope),
      JSON.stringify({ ...draft, savedAt })
    );
  } catch {
    // 容量超過等は無視 (DB 保存が本線)
  }
}

interface LegacyKpt {
  keep: string;
  problem: string;
  tryNext: string;
}

function parseLegacyKpt(raw: string | null): LegacyKpt | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const p = parsed as Record<string, unknown>;
    return {
      keep: toStringField(p.keep),
      problem: toStringField(p.problem),
      tryNext: toStringField(p.tryNext),
    };
  } catch {
    return null;
  }
}

/**
 * 旧 KPT キー (donezo-kpt-{mode}-{periodStart}[-{notebookId}]) を1つのドラフトへ統合する。
 * ノートブック別キーは [ノートブック名] プレフィックス付きで freeNote へ追記。
 * 移行対象データが無ければ null。
 */
export function collectLegacyKpt(
  storage: StorageLike,
  mode: RetroMode,
  periodStart: string,
  notebookNameById: Map<string, string>
): { draft: RetroDraft; keysToRemove: string[] } | null {
  const baseKey = `donezo-kpt-${mode}-${periodStart}`;
  const notebookPrefix = `${baseKey}-`;
  const draft = emptyDraft();
  const keysToRemove: string[] = [];

  const base = parseLegacyKpt(storage.getItem(baseKey));
  if (base) {
    draft.keep = base.keep;
    draft.problem = base.problem;
    draft.tryNext = base.tryNext;
    keysToRemove.push(baseKey);
  }

  // ノートブック別キーを走査 (dateKey は YYYY-MM-DD 固定長なので prefix 一致で安全)
  const notebookKeys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && key.startsWith(notebookPrefix)) notebookKeys.push(key);
  }
  notebookKeys.sort();

  const noteParts: string[] = [];
  for (const key of notebookKeys) {
    const kpt = parseLegacyKpt(storage.getItem(key));
    if (!kpt) continue; // 壊れたキーはデータ喪失回避のため残す
    const notebookId = key.slice(notebookPrefix.length);
    const name = notebookNameById.get(notebookId) ?? notebookId;
    const lines: string[] = [];
    if (kpt.keep) lines.push(`Keep: ${kpt.keep}`);
    if (kpt.problem) lines.push(`Problem: ${kpt.problem}`);
    if (kpt.tryNext) lines.push(`Try: ${kpt.tryNext}`);
    if (lines.length > 0) noteParts.push(`[${name}]\n${lines.join("\n")}`);
    keysToRemove.push(key);
  }
  draft.freeNote = noteParts.join("\n\n");

  if (keysToRemove.length === 0 || isDraftEmpty(draft)) return null;
  return { draft, keysToRemove };
}

/** 前期間リマインド用: 旧キーから tryNext のみ読む (移行はしない) */
export function readLegacyTryNext(
  storage: StorageLike,
  mode: RetroMode,
  periodStart: string
): string {
  return parseLegacyKpt(
    storage.getItem(`donezo-kpt-${mode}-${periodStart}`)
  )?.tryNext ?? "";
}

/** Supabase の snake_case 行を camelCase レコードへ変換 */
export function mapRetrospectiveRow(row: any): RetrospectiveRecord {
  return {
    id: row.id,
    mode: row.mode,
    periodStart: row.period_start,
    keep: row.keep ?? "",
    problem: row.problem ?? "",
    tryNext: row.try_next ?? "",
    freeNote: row.free_note ?? "",
    updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : 0,
  };
}
